import React, { useState, useEffect } from 'react';
import {
    Box, Container, Tabs, Tab, Paper, Button, Typography,
    useTheme, alpha, Grow
} from '@mui/material';
import {
    Schedule, CalendarToday, AllInclusive, Refresh
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getLeaderboard, getUserStats } from '../api/rewards';
import LeaderboardHero from '../components/Leaderboard/LeaderboardHero';
import LeaderboardTabPanel from '../components/Leaderboard/LeaderboardTabPanel';
import AuthPrompt from '../components/common/AuthPrompt';

const LeaderboardPage = () => {
    const { user, isAuthenticated } = useAuth();
    const theme = useTheme();

    // State management
    const [currentTab, setCurrentTab] = useState(0);
    const [leaderboardData, setLeaderboardData] = useState({
        weekly: { data: [], loading: false, error: null, page: 1, totalPages: 0 },
        monthly: { data: [], loading: false, error: null, page: 1, totalPages: 0 },
        alltime: { data: [], loading: false, error: null, page: 1, totalPages: 0 }
    });
    const [userStats, setUserStats] = useState(null);
    // Set different default page sizes based on authentication
    const [pageSize, setPageSize] = useState(isAuthenticated ? 10 : 10);
    const [sortBy, setSortBy] = useState('totalPoints,desc');
    const [heroChampions, setHeroChampions] = useState([]);
    const [totalParticipants, setTotalParticipants] = useState(0);

    // Tab configuration
    const tabs = [
        {
            label: 'Weekly',
            period: 'weekly',
            icon: <Schedule />,
            color: '#66bb6a',
            description: 'This week\'s environmental heroes'
        },
        {
            label: 'Monthly',
            period: 'monthly',
            icon: <CalendarToday />,
            color: '#43a047',
            description: 'This month\'s eco leaders'
        },
        {
            label: 'All Time',
            period: 'alltime',
            icon: <AllInclusive />,
            color: '#2e7d32',
            description: 'Greatest eco warriors of all time'
        }
    ];

    // Fetch user stats if authenticated
    useEffect(() => {
        if (isAuthenticated) {
            fetchUserStats();
        }
    }, [isAuthenticated]);

    // Fetch leaderboard data when tab changes
    useEffect(() => {
        const currentPeriod = tabs[currentTab].period;
        // Use correct default sorting based on period
        const defaultSort = currentPeriod === 'alltime' ? 'rewardsPoints,desc' : 'totalPoints,desc';
        setSortBy(defaultSort); // Update sortBy state to match period
        fetchLeaderboardData(currentPeriod, 1, pageSize, defaultSort);
    }, [currentTab, pageSize]); // Remove sortBy from dependencies to avoid infinite loop

    // Fetch all-time data for hero section
    useEffect(() => {
        fetchHeroData();
    }, []);

    const fetchUserStats = async () => {
        if (!isAuthenticated) return;

        try {
            const response = await getUserStats();
            setUserStats(response.data);
        } catch (error) {
            console.warn('Failed to fetch user stats:', error);
        }
    };

    const fetchLeaderboardData = async (period, page = 1, size = pageSize, sort = sortBy, showLoading = true) => {
        if (showLoading) {
            setLeaderboardData(prev => ({
                ...prev,
                [period]: { ...prev[period], loading: true, error: null }
            }));
        }

        try {
            // Adjust size based on authentication status
            const actualSize = !isAuthenticated ? Math.min(size, 50) : size;
            const response = await getLeaderboard(period, page - 1, actualSize, sort);
            const { content, totalPages, totalElements, number } = response.data;

            // Process data to mark current user
            const processedData = content.map(entry => ({
                ...entry,
                isCurrentUser: isAuthenticated && user &&
                    (entry.userId === user.id || entry.displayName === user.displayName)
            }));

            setLeaderboardData(prev => ({
                ...prev,
                [period]: {
                    data: processedData,
                    loading: false,
                    error: null,
                    page: number + 1,
                    totalPages,
                    totalElements
                }
            }));

            // Update total participants from all-time data
            if (period === 'alltime') {
                setTotalParticipants(totalElements);
            }

        } catch (error) {
            console.error(`Failed to fetch ${period} leaderboard:`, error);

            setLeaderboardData(prev => ({
                ...prev,
                [period]: {
                    ...prev[period],
                    loading: false,
                    error: 'Unable to load leaderboard data. Please try again later.'
                }
            }));
        }
    };

    const fetchHeroData = async () => {
        try {
            // Use correct sorting for all-time leaderboard
            const response = await getLeaderboard('alltime', 0, 3, 'rewardsPoints,desc');
            setHeroChampions(response.data.content || []);
        } catch (error) {
            console.error('Failed to fetch hero data:', error);
            setHeroChampions([]);
        }
    };


    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const handlePageChange = (period, newPage) => {
        fetchLeaderboardData(period, newPage, pageSize, sortBy);
    };

    const handlePageSizeChange = (period, newSize) => {
        setPageSize(newSize);
        fetchLeaderboardData(period, 1, newSize, sortBy);
    };

    const handleSortChange = (period, newSort) => {
        setSortBy(newSort);
        fetchLeaderboardData(period, 1, pageSize, newSort);
    };

    const handleRefresh = () => {
        const currentPeriod = tabs[currentTab].period;
        const currentData = leaderboardData[currentPeriod];
        fetchLeaderboardData(currentPeriod, currentData.page, pageSize, sortBy, false);
        if (isAuthenticated) {
            fetchUserStats();
        }
        fetchHeroData();
    };

    const currentPeriod = tabs[currentTab].period;
    const currentData = leaderboardData[currentPeriod];
    const currentTabConfig = tabs[currentTab];

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8faf8 0%, #e6f4ea 100%)' }}>
            {/* Hero Section */}
            <LeaderboardHero
                topChampions={heroChampions}
                totalParticipants={totalParticipants}
                isAuthenticated={isAuthenticated}
                userRank={userStats?.globalRank}
                userStats={userStats}
            />

            {/* Main Content */}
            <Container maxWidth="lg" sx={{ py: 6 }}>
                {/* Authentication Prompt for Public Users */}
                <AuthPrompt isAuthenticated={isAuthenticated} />

                {/* Tabs Section */}
                <Paper
                    elevation={8}
                    sx={{
                        borderRadius: 6,
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fff8 100%)',
                        border: '1px solid rgba(67, 160, 71, 0.1)'
                    }}
                >
                    {/* Tab Headers */}
                    <Box sx={{
                        background: `linear-gradient(135deg, ${alpha(currentTabConfig.color, 0.1)} 0%, ${alpha(currentTabConfig.color, 0.05)} 100%)`,
                        borderBottom: `1px solid ${alpha(currentTabConfig.color, 0.2)}`
                    }}>
                        <Tabs
                            value={currentTab}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            sx={{
                                '& .MuiTab-root': {
                                    fontWeight: 600,
                                    minHeight: 80,
                                    color: theme.palette.text.secondary,
                                    transition: 'all 0.3s ease',
                                    py: 2,
                                    '&:hover': {
                                        background: alpha(currentTabConfig.color, 0.1),
                                        color: currentTabConfig.color
                                    }
                                },
                                '& .Mui-selected': {
                                    color: `${currentTabConfig.color} !important`,
                                    background: alpha(currentTabConfig.color, 0.1),
                                    fontWeight: 800
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: currentTabConfig.color,
                                    height: 4,
                                    borderRadius: '2px 2px 0 0'
                                }
                            }}
                        >
                            {tabs.map((tab, index) => (
                                <Tab
                                    key={tab.period}
                                    icon={tab.icon}
                                    label={
                                        <Box>
                                            <Typography variant="h6" fontWeight="inherit">
                                                {tab.label}
                                            </Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                {tab.description}
                                            </Typography>
                                        </Box>
                                    }
                                    iconPosition="top"
                                />
                            ))}
                        </Tabs>

                        {/* Refresh Button */}
                        <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleRefresh}
                                startIcon={<Refresh />}
                                sx={{
                                    borderColor: currentTabConfig.color,
                                    color: currentTabConfig.color,
                                    '&:hover': {
                                        background: alpha(currentTabConfig.color, 0.1),
                                        borderColor: currentTabConfig.color
                                    }
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Box>

                    {/* Tab Content */}
                    <Box sx={{ minHeight: 600, position: 'relative' }}>
                        {tabs.map((tab, index) => (
                            <LeaderboardTabPanel
                                key={tab.period}
                                data={leaderboardData[tab.period].data}
                                loading={leaderboardData[tab.period].loading}
                                error={leaderboardData[tab.period].error}
                                tabIndex={index}
                                currentTab={currentTab}
                                period={tab.period}
                                isAuthenticated={isAuthenticated}
                                onRefresh={() => handleRefresh()}
                                page={leaderboardData[tab.period].page}
                                totalPages={leaderboardData[tab.period].totalPages}
                                pageSize={pageSize}
                                onPageChange={(newPage) => handlePageChange(tab.period, newPage)}
                                onPageSizeChange={(newSize) => handlePageSizeChange(tab.period, newSize)}
                                sortBy={sortBy}
                                onSortChange={(newSort) => handleSortChange(tab.period, newSort)}
                                animateCards={true}
                            />
                        ))}
                    </Box>
                </Paper>

                {/* Bottom CTA for Public Users */}
                {!isAuthenticated && (
                    <Grow in={true} timeout={1500}>
                        <Paper
                            elevation={12}
                            sx={{
                                mt: { xs: 4, sm: 6 },
                                p: { xs: 3, sm: 4, md: 6 },
                                textAlign: 'center',
                                background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
                                color: 'white',
                                borderRadius: { xs: 3, sm: 6 },
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 50%)',
                                    pointerEvents: 'none'
                                }
                            }}
                        >
                            <Typography
                                variant="h1"
                                sx={{
                                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                                    mb: { xs: 2, sm: 3 }
                                }}
                            >
                                🌍
                            </Typography>
                            <Typography
                                variant="h3"
                                fontWeight={800}
                                sx={{
                                    mb: { xs: 2, sm: 3 },
                                    fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' }
                                }}
                            >
                                Ready to Make a Difference?
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    mb: { xs: 3, sm: 4 },
                                    opacity: 0.9,
                                    maxWidth: 600,
                                    mx: 'auto',
                                    fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
                                    lineHeight: { xs: 1.4, sm: 1.5 },
                                    px: { xs: 1, sm: 0 }
                                }}
                            >
                                Join thousands of environmental champions in Bangladesh.
                                <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                                    {' '}Start classifying waste, earn points, unlock badges, and help create a cleaner future!
                                </Box>
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: { xs: 2, sm: 2 },
                                justifyContent: 'center',
                                alignItems: 'center',
                                maxWidth: { xs: '100%', sm: 'none' }
                            }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    href="/register"
                                    sx={{
                                        background: 'rgba(255,255,255,0.2)',
                                        backdropFilter: 'blur(10px)',
                                        color: 'white',
                                        fontWeight: 700,
                                        px: { xs: 3, sm: 4 },
                                        py: { xs: 1.5, sm: 2 },
                                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                                        border: '2px solid rgba(255,255,255,0.3)',
                                        width: { xs: '100%', sm: 'auto' },
                                        maxWidth: { xs: '280px', sm: 'none' },
                                        '&:hover': {
                                            background: 'rgba(255,255,255,0.3)',
                                            transform: { xs: 'none', sm: 'translateY(-2px)' },
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                                        }
                                    }}
                                >
                                    🚀 Start Your Eco Journey
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    href="/classify"
                                    sx={{
                                        borderColor: 'rgba(255,255,255,0.8)',
                                        color: 'white',
                                        fontWeight: 700,
                                        px: { xs: 3, sm: 4 },
                                        py: { xs: 1.5, sm: 2 },
                                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                                        borderWidth: '2px',
                                        width: { xs: '100%', sm: 'auto' },
                                        maxWidth: { xs: '280px', sm: 'none' },
                                        '&:hover': {
                                            borderColor: 'white',
                                            background: 'rgba(255,255,255,0.1)',
                                            borderWidth: '2px',
                                            transform: { xs: 'none', sm: 'translateY(-2px)' }
                                        }
                                    }}
                                >
                                    🔍 Try Classification
                                </Button>
                            </Box>
                        </Paper>
                    </Grow>
                )}
            </Container>
        </Box>
    );
};

export default LeaderboardPage;
